import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import { 
  RAADS_R_QUESTIONS, 
  OPTIONS, 
  SUBSCALES, 
  calculateRaadsRScore 
} from '../data/raadsRData';
import { 
  Clock, 
  ShieldCheck, 
  LockKeyhole, 
  FileText, 
  ChevronRight, 
  ChevronLeft, 
  Info, 
  Users, 
  MessagesSquare, 
  Brain, 
  Activity, 
  Target, 
  RotateCcw, 
  ArrowRight, 
  AlertCircle, 
  CheckCircle2 
} from 'lucide-react';
import ReadMoreAutismSection from '../components/ReadMoreAutismSection';

export default function RaadsRScreening({ isEmbedded = false }) {
  const { lang, getLocalizedValue, localizePath } = useLanguage();

  const [cmsContent, setCmsContent] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchCmsContent = async () => {
      try {
        let res;
        try {
          res = await fetch('/_api/public/content/autisme_screening_side');
          if (!res.ok) throw new Error('relative fetch failed');
        } catch {
          res = await fetch('https://api.dev.auxo.dk/public/content/autisme_screening_side');
        }
        if (res.ok) {
          const data = await res.json();
          if (isMounted && Array.isArray(data) && data.length > 0) {
            setCmsContent(data[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching autisme_screening_side:', err);
      }
    };

    fetchCmsContent();
    return () => {
      isMounted = false;
    };
  }, []);

  const seoTitle = lang === 'da'
    ? 'RAADS-R Autisme-screening (80 spørgsmål) | Nordisk Psykiatri'
    : 'RAADS-R Autismscreening (80 frågor) | Nordisk Psykiatri';
  const seoDesc = lang === 'da'
    ? 'Tag den officielle danske RAADS-R selvtest for autisme. Anonym og vejledende screening med beregning af samlet score og fire delskalaer.'
    : 'Gör det officiella RAADS-R självtestet för autism. Anonym och vägledande screening med beräkning av totalpoäng och fyra delskalor.';

  useDocumentMetadata(!isEmbedded ? seoTitle : '', !isEmbedded ? seoDesc : '', { lang });

  // State
  const [currentStep, setCurrentStep] = useState(0); // 0: Intro, 1-80: Questions, 81: Results
  const [answers, setAnswers] = useState({}); // { questionId (1-80): optionIndex (0-3) }
  const [errorMsg, setErrorMsg] = useState('');

  const topRef = useRef(null);
  const testRef = useRef(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Prevent scrolling on initial page load / component mount
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const headerOffset = 120; // Accounts for sticky menu height

    if (currentStep >= 1 && testRef.current) {
      const elementPosition = testRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: Math.max(0, offsetPosition),
        behavior: 'smooth'
      });
    } else if (currentStep === 0 && topRef.current) {
      const elementPosition = topRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: Math.max(0, offsetPosition),
        behavior: 'smooth'
      });
    }
  }, [currentStep]);

  const handleOptionSelect = (qId, optionIdx) => {
    setAnswers(prev => ({ ...prev, [qId]: optionIdx }));
    setErrorMsg('');
  };

  const handleStartScreening = () => {
    setCurrentStep(1);
    setErrorMsg('');
  };

  const handleNext = () => {
    if (currentStep === 0) {
      setCurrentStep(1);
      return;
    }

    if (currentStep >= 1 && currentStep <= 80) {
      if (answers[currentStep] === undefined || answers[currentStep] === null) {
        setErrorMsg('Vælg venligst en svarmulighed for at fortsætte.');
        return;
      }

      if (currentStep < 80) {
        setCurrentStep(prev => prev + 1);
        setErrorMsg('');
      } else {
        // Calculate result on Q80
        setCurrentStep(81);
        setErrorMsg('');
      }
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      setErrorMsg('');
    } else if (currentStep === 1) {
      setCurrentStep(0);
      setErrorMsg('');
    }
  };

  const handleReset = () => {
    setAnswers({});
    setCurrentStep(0);
    setErrorMsg('');
  };

  const currentQ = RAADS_R_QUESTIONS[currentStep - 1];
  const { totalScore, subscaleScores } = calculateRaadsRScore(answers);

  // Determine active domain for question 1-80
  const getActiveDomain = (qNum) => {
    if (!qNum || qNum < 1 || qNum > 80) return 'social';
    if (SUBSCALES.social.questions.includes(qNum)) return 'social';
    if (SUBSCALES.language.questions.includes(qNum)) return 'language';
    if (SUBSCALES.sensory.questions.includes(qNum)) return 'sensory';
    if (SUBSCALES.interests.questions.includes(qNum)) return 'interests';
    return 'social';
  };

  const activeDomainKey = getActiveDomain(currentStep);

  const resolveText = (field, fallbackDa, fallbackSv) => {
    if (cmsContent && cmsContent[field]) {
      const val = getLocalizedValue(cmsContent[field]);
      if (typeof val === 'string' && val.trim().length > 0) return val;
    }
    return lang === 'da' ? fallbackDa : fallbackSv;
  };

  const introBadge = resolveText('intro_badge', 'AUTISME SELVSCREENING', 'AUTISM SELF-SCREENING');
  const introOverskrift = resolveText('intro_overskrift', 'Er du i tvivl, om du kan have autistiske træk?', 'Do you wonder if you might have autistic traits?');
  const introBeskrivelse = resolveText(
    'intro_beskrivelse',
    'Ritvo Autism Asperger Diagnostic Scale-Revised (RAADS-R) er et anerkendt og videnskabeligt valideret screeningsværktøj med 80 spørgsmål opdelt i fire faglige delskalaer. Testen vedrører autistiske træk og adfærdsmønstre i voksenlivet og opvæksten. Resultatet kan give et første overblik og bruges som grundlag for refleksion og en faglig samtale hos os. Testen kan ikke stå alene som en diagnose.',
    'Ritvo Autism Asperger Diagnostic Scale-Revised (RAADS-R) is a recognised and scientifically validated screening tool with 80 questions divided into four clinical subscales. The test covers autistic traits and behavioral patterns in adult life and during development. The result provides an initial overview and a basis for reflection and clinical discussion with us. The screening cannot stand alone as a diagnosis.'
  );
  const infoBoksTitel = resolveText('infoboks_titel', 'Screening, ikke en diagnose:', 'Screening, not a diagnosis:');
  const infoBoksTekst = resolveText(
    'infoboks_tekst',
    'RAADS-R er et screeningsværktøj og kan ikke i sig selv stille en autismediagnose. Resultatet skal altid vurderes sammen med din udviklingshistorie, aktuelle forhold og en samlet klinisk vurdering af en autoriseret fagperson.',
    'RAADS-R is a screening instrument and cannot independently establish an autism diagnosis. The results must always be evaluated in conjunction with your developmental history, current circumstances, and a comprehensive clinical assessment by a licensed professional.'
  );

  return (
    <div ref={topRef} className={`${isEmbedded ? 'bg-transparent pt-0 pb-0' : 'bg-sand-light py-10 lg:py-16'} text-text-dark`}>
      <div className="mx-auto max-w-7xl px-6 lg:px-8 space-y-12">
        
        {/* ==================================================================== */}
        {/* 2. RAADS-R-INTRODUKTION & 3. PRAKTISKE INFO & 4. STARTKNAP */}
        {/* ==================================================================== */}
        <section className="bg-sand-medium/20 border border-sand-medium/50 rounded-2xl p-6 sm:p-8 lg:p-10 space-y-8">
          
          <div className="space-y-4 max-w-3xl">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-petrol-medium block">
              {introBadge}
            </span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-petrol-dark leading-tight">
              {introOverskrift}
            </h2>
            {typeof introBeskrivelse === 'string' && introBeskrivelse.includes('<') ? (
              <div 
                className="text-xs sm:text-sm text-text-muted leading-relaxed [&>p]:mb-2 [&>p:last-child]:mb-0"
                dangerouslySetInnerHTML={{ __html: introBeskrivelse }}
              />
            ) : (
              <p className="text-xs sm:text-sm text-text-muted leading-relaxed">
                {introBeskrivelse}
              </p>
            )}

            {/* Diskret informationspanel */}
            <div className="bg-white/90 border border-sand-medium/60 rounded-xl p-4 flex items-start gap-3.5 text-xs sm:text-sm text-text-muted leading-relaxed">
              <Info className="w-5 h-5 text-petrol-medium shrink-0 mt-0.5" />
              <div>
                <strong className="text-petrol-dark block mb-0.5 font-semibold">
                  {infoBoksTitel}
                </strong>
                {infoBoksTekst}
              </div>
            </div>
          </div>

          {/* 4 Praktiske Informationer */}
          <div className="border-t border-sand-medium/40 pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              
              <div className="bg-white/70 border border-sand-medium/50 rounded-xl p-4 flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-full bg-sand-medium/30 text-petrol-dark flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold text-xs sm:text-sm text-petrol-dark">
                    {lang === 'da' ? 'Ca. 10–15 minutter' : 'Approx. 10–15 minutes'}
                  </div>
                  <div className="text-[11px] text-text-muted">
                    {lang === 'da' ? 'Estimeret tidsforbrug' : 'Estimated time'}
                  </div>
                </div>
              </div>

              <div className="bg-white/70 border border-sand-medium/50 rounded-xl p-4 flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-full bg-sand-medium/30 text-petrol-dark flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold text-xs sm:text-sm text-petrol-dark">
                    {lang === 'da' ? 'Videnskabeligt valideret' : 'Scientifically validated'}
                  </div>
                  <div className="text-[11px] text-text-muted">
                    {lang === 'da' ? 'Anerkendt RAADS-R skema' : 'Recognised RAADS-R scale'}
                  </div>
                </div>
              </div>

              <div className="bg-white/70 border border-sand-medium/50 rounded-xl p-4 flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-full bg-sand-medium/30 text-petrol-dark flex items-center justify-center shrink-0">
                  <LockKeyhole className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold text-xs sm:text-sm text-petrol-dark">
                    {lang === 'da' ? 'Anonymt og fortroligt' : 'Anonymous and confidential'}
                  </div>
                  <div className="text-[11px] text-text-muted">
                    {lang === 'da' ? 'Dine svar gemmes ikke' : 'Your answers are not stored'}
                  </div>
                </div>
              </div>

              <div className="bg-white/70 border border-sand-medium/50 rounded-xl p-4 flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-full bg-sand-medium/30 text-petrol-dark flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold text-xs sm:text-sm text-petrol-dark">
                    {lang === 'da' ? '80 spørgsmål' : '80 questions'}
                  </div>
                  <div className="text-[11px] text-text-muted">
                    {lang === 'da' ? 'Opdelt i 4 delområder' : 'Divided into 4 subscales'}
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Startknap */}
          {currentStep === 0 && (
            <div className="pt-2">
              <button
                type="button"
                onClick={handleStartScreening}
                className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-petrol-dark text-white font-semibold text-sm rounded-full hover:bg-petrol-medium transition-colors cursor-pointer shadow-xs"
              >
                <span>{lang === 'da' ? 'Start RAADS-R selvscreening' : 'Start RAADS-R self-screening'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

        </section>

        {/* ==================================================================== */}
        {/* 5. TESTOMRÅDE (SPØRGSMÅL 1 TIL 80) */}
        {/* ==================================================================== */}
        {currentStep >= 1 && currentStep <= 80 && (
          <div ref={testRef} className="bg-white border border-sand-medium/60 rounded-2xl shadow-xs overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-12">
              
              {/* VENSTRE FREMSKRIDTSKOLONNE (ca. 25-28% bredde) */}
              <div className="lg:col-span-4 bg-sand-light/40 border-b lg:border-b-0 lg:border-r border-sand-medium/50 p-6 sm:p-8 flex flex-col justify-between space-y-8">
                
                <div className="space-y-6">
                  <div>
                    <h3 className="font-display font-semibold text-lg text-petrol-dark mb-1">
                      {lang === 'da' ? 'Fremgang' : 'Progress'}
                    </h3>
                    <p className="text-xs text-petrol-medium font-medium">
                      {lang === 'da' ? `Spørgsmål ${currentStep} af 80` : `Question ${currentStep} of 80`}
                    </p>
                  </div>

                  {/* Progressbar & Procent */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center text-xs font-semibold text-petrol-dark">
                      <span>Status</span>
                      <span>{Math.round((currentStep / 80) * 100)}%</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-sand-medium/30 overflow-hidden">
                      <div 
                        className="h-full bg-petrol-dark transition-all duration-300 rounded-full"
                        style={{ width: `${(currentStep / 80) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Visuel opdeling i fire faglige områder */}
                  <div className="border-t border-sand-medium/40 pt-5 space-y-3">
                    <div className="text-[11px] font-mono font-semibold tracking-wider uppercase text-petrol-medium mb-1">
                      {lang === 'da' ? 'DELOMRÅDER I TESTEN' : 'SUBSCALES IN TEST'}
                    </div>

                    {/* Domain 1: Socialt samspil */}
                    <div className={`flex items-center gap-3 text-xs p-2 rounded-lg transition-colors ${
                      activeDomainKey === 'social' ? 'bg-sand-medium/30 font-semibold text-petrol-dark' : 'text-text-muted'
                    }`}>
                      <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                        activeDomainKey === 'social' ? 'bg-petrol-dark' : 'border border-sand-medium/80 bg-white'
                      }`} />
                      <span>{lang === 'da' ? 'Socialt samspil og relationer' : 'Social interaction & relationships'}</span>
                    </div>

                    {/* Domain 2: Kommunikation / Sprog */}
                    <div className={`flex items-center gap-3 text-xs p-2 rounded-lg transition-colors ${
                      activeDomainKey === 'language' ? 'bg-sand-medium/30 font-semibold text-petrol-dark' : 'text-text-muted'
                    }`}>
                      <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                        activeDomainKey === 'language' ? 'bg-petrol-dark' : 'border border-sand-medium/80 bg-white'
                      }`} />
                      <span>{lang === 'da' ? 'Sprog og kommunikation' : 'Language & communication'}</span>
                    </div>

                    {/* Domain 3: Sensorik og motorik */}
                    <div className={`flex items-center gap-3 text-xs p-2 rounded-lg transition-colors ${
                      activeDomainKey === 'sensory' ? 'bg-sand-medium/30 font-semibold text-petrol-dark' : 'text-text-muted'
                    }`}>
                      <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                        activeDomainKey === 'sensory' ? 'bg-petrol-dark' : 'border border-sand-medium/80 bg-white'
                      }`} />
                      <span>{lang === 'da' ? 'Sensorik og motorik' : 'Sensory & motor processing'}</span>
                    </div>

                    {/* Domain 4: Afgrænsede interesser */}
                    <div className={`flex items-center gap-3 text-xs p-2 rounded-lg transition-colors ${
                      activeDomainKey === 'interests' ? 'bg-sand-medium/30 font-semibold text-petrol-dark' : 'text-text-muted'
                    }`}>
                      <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                        activeDomainKey === 'interests' ? 'bg-petrol-dark' : 'border border-sand-medium/80 bg-white'
                      }`} />
                      <span>{lang === 'da' ? 'Afgrænsede interesser' : 'Circumscribed interests'}</span>
                    </div>
                  </div>
                </div>

                {/* Nederst: Privatlivspanel */}
                <div className="bg-white/80 border border-sand-medium/50 rounded-xl p-3.5 flex items-start gap-2.5 text-xs text-text-muted">
                  <LockKeyhole className="w-4 h-4 text-petrol-medium shrink-0 mt-0.5" />
                  <span>
                    {lang === 'da' 
                      ? 'Dine svar behandles 100% anonymt i din browser og gemmes ikke.' 
                      : 'Your answers are processed 100% anonymously in your browser and are not saved.'}
                  </span>
                </div>

              </div>

              {/* HØJRE SPØRGSMÅLSOMRÅDE (ca. 72-75% bredde) */}
              <div className="lg:col-span-8 p-6 sm:p-8 lg:p-10 flex flex-col justify-between space-y-8">
                
                <div>
                  <div className="flex items-center justify-between text-xs font-mono text-petrol-medium mb-3">
                    <span>{lang === 'da' ? `SPØRGSMÅL ${currentStep} AF 80` : `QUESTION ${currentStep} OF 80`}</span>
                    <span className="font-semibold text-petrol-dark">RAADS-R</span>
                  </div>

                  <h2 className="font-display text-xl sm:text-2xl font-medium text-petrol-dark leading-snug mb-8 max-w-2xl">
                    {currentQ.text}
                  </h2>

                  {/* Svarmuligheder i lodret liste */}
                  <div className="space-y-3" role="radiogroup" aria-label={lang === 'da' ? `Spørgsmål ${currentStep}` : `Question ${currentStep}`}>
                    {(lang === 'da' ? OPTIONS : [
                      'True now and when I was young',
                      'True only now',
                      'True only when I was young',
                      'Never true'
                    ]).map((optLabel, optIdx) => {
                      const isSelected = answers[currentStep] === optIdx;
                      return (
                        <label
                          key={optIdx}
                          onClick={() => handleOptionSelect(currentStep, optIdx)}
                          className={`w-full text-left flex items-center p-4 rounded-xl border transition-all cursor-pointer ${
                            isSelected
                              ? 'bg-petrol-dark/5 border-petrol-dark text-petrol-dark shadow-xs'
                              : 'bg-white border-sand-medium/60 text-petrol-dark hover:bg-sand-medium/20 hover:border-petrol-medium/50'
                          }`}
                        >
                          <input
                            type="radio"
                            name={`question-${currentStep}`}
                            checked={isSelected}
                            onChange={() => handleOptionSelect(currentStep, optIdx)}
                            className="sr-only"
                          />
                          <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 mr-4 transition-colors ${
                            isSelected ? 'border-petrol-dark bg-petrol-dark' : 'border-sand-medium/80 bg-white'
                          }`}>
                            {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                          <span className="font-medium text-sm sm:text-base leading-snug">{optLabel}</span>
                        </label>
                      );
                    })}
                  </div>

                  {/* Fejlbesked ved manglende valg */}
                  {errorMsg && (
                    <div className="mt-4 p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs sm:text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{errorMsg}</span>
                    </div>
                  )}
                </div>

                {/* Navigation i testen */}
                <div className="flex items-center justify-between border-t border-sand-medium/40 pt-6">
                  <button
                    type="button"
                    onClick={handlePrev}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-sand-medium/80 text-petrol-dark font-medium text-xs sm:text-sm hover:bg-sand-light transition-colors cursor-pointer"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>{lang === 'da' ? 'Forrige' : 'Previous'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleNext}
                    className="inline-flex items-center gap-2 px-7 py-2.5 rounded-full bg-petrol-dark text-white font-semibold text-xs sm:text-sm hover:bg-petrol-medium transition-colors cursor-pointer shadow-xs"
                  >
                    <span>{currentStep === 80 ? (lang === 'da' ? 'Se resultat' : 'See result') : (lang === 'da' ? 'Næste' : 'Next')}</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

              </div>

            </div>
          </div>
        )}

        {/* ==================================================================== */}
        {/* 6. RESULTATSEKTION (STEP 81) */}
        {/* ==================================================================== */}
        {currentStep === 81 && (
          <div className="space-y-8">
            
            {/* Overskrift */}
            <div className="text-center max-w-2xl mx-auto space-y-2">
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-petrol-medium block">
                {lang === 'da' ? 'SCREENING FÆRDIG' : 'SCREENING COMPLETED'}
              </span>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-petrol-dark">
                {lang === 'da' ? 'Efter screeningen – dit resultat' : 'After the screening – your result'}
              </h2>
              <p className="text-xs sm:text-sm text-text-muted leading-relaxed">
                {lang === 'da' 
                  ? 'Her er overblikket over dine samlede besvarelser og delresultater på RAADS-R selvscreeningen.' 
                  : 'Here is an overview of your total responses and subscale results from the RAADS-R self-screening.'}
              </p>
            </div>

            {/* Tredelt Resultat Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              
              {/* VESTRE PANEL: Scoringsoverblik (lg:col-span-5) */}
              <div className="lg:col-span-5 bg-white border border-sand-medium/60 rounded-2xl p-6 sm:p-8 space-y-6 shadow-xs">
                <div>
                  <h3 className="font-display font-semibold text-xl text-petrol-dark mb-1">
                    {lang === 'da' ? 'Scoringsoverblik' : 'Score Overview'}
                  </h3>
                  <p className="text-xs text-text-muted">
                    {lang === 'da' 
                      ? 'Samlet score samt opdeling på de fire faglige delskalaer.' 
                      : 'Total score and breakdown across the four clinical subscales.'}
                  </p>
                </div>

                {/* Samlet score fremhævet */}
                <div className="bg-sand-medium/20 border border-sand-medium/50 rounded-xl p-5 space-y-3">
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs font-semibold uppercase tracking-wider text-petrol-medium">
                      {lang === 'da' ? 'SAMLET RAADS-R SCORE' : 'TOTAL RAADS-R SCORE'}
                    </span>
                    <span className="font-display font-bold text-2xl text-petrol-dark">
                      {totalScore} <span className="text-xs text-text-muted font-normal">{lang === 'da' ? '/ 240 pt' : '/ 240 pts'}</span>
                    </span>
                  </div>

                  <div className="w-full h-2.5 rounded-full bg-sand-medium/40 overflow-hidden">
                    <div 
                      className="h-full bg-petrol-dark transition-all duration-500 rounded-full"
                      style={{ width: `${(totalScore / 240) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Delskalaer med vandrette bjælker */}
                <div className="space-y-4 pt-2">
                  <span className="text-xs font-mono font-semibold tracking-wider text-petrol-medium uppercase block">
                    {lang === 'da' ? 'DELSKALAER' : 'SUBSCALES'}
                  </span>

                  {Object.values(SUBSCALES).map((sub) => {
                    const score = subscaleScores[sub.key] || 0;
                    const pct = Math.round((score / sub.maxScore) * 100);

                    const subTitles = {
                      social: lang === 'da' ? 'Social forståelse og relationer' : 'Social understanding & relationships',
                      language: lang === 'da' ? 'Sprog' : 'Language & communication',
                      interests: lang === 'da' ? 'Afgrænsede interesser' : 'Circumscribed interests',
                      sensory: lang === 'da' ? 'Sensorik og motorik' : 'Sensory & motor processing'
                    };

                    return (
                      <div key={sub.key} className="space-y-1.5">
                        <div className="flex justify-between items-center text-xs">
                          <span className="font-semibold text-petrol-dark">{subTitles[sub.key] || sub.title}</span>
                          <span className="font-mono text-text-muted">{score} / {sub.maxScore} {lang === 'da' ? 'pt' : 'pts'}</span>
                        </div>
                        <div className="w-full h-2 rounded-full bg-sand-medium/30 overflow-hidden">
                          <div 
                            className="h-full bg-petrol-medium transition-all duration-500 rounded-full"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Tredelt scoreforklaring legend */}
                <div className="border-t border-sand-medium/40 pt-4 space-y-2 text-xs text-text-muted">
                  <div className="font-semibold text-petrol-dark mb-1">
                    {lang === 'da' ? 'Generelle referenceintervaller:' : 'General reference ranges:'}
                  </div>
                  <div className="flex justify-between items-center">
                    <span>{lang === 'da' ? 'Under 65 point:' : 'Below 65 points:'}</span>
                    <span className="font-medium text-petrol-dark">
                      {lang === 'da' ? 'Lav grad af træk' : 'Low level of traits'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>{lang === 'da' ? '65 – 120 point:' : '65 – 120 points:'}</span>
                    <span className="font-medium text-petrol-dark">
                      {lang === 'da' ? 'Moderat grad af træk' : 'Moderate level of traits'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>{lang === 'da' ? 'Over 120 point:' : 'Above 120 points:'}</span>
                    <span className="font-medium text-petrol-dark">
                      {lang === 'da' ? 'Høj grad af træk' : 'High level of traits'}
                    </span>
                  </div>
                </div>
              </div>

              {/* MIDTERPANEL: "Hvad betyder resultatet?" (lg:col-span-4) */}
              <div className="lg:col-span-4 bg-white border border-sand-medium/60 rounded-2xl p-6 sm:p-8 flex flex-col justify-between space-y-6 shadow-xs">
                <div className="space-y-4">
                  <h3 className="font-display font-semibold text-xl text-petrol-dark">
                    {lang === 'da' ? 'Hvad betyder resultatet?' : 'What does the result mean?'}
                  </h3>

                  <div className="text-xs sm:text-sm text-text-muted leading-relaxed space-y-3">
                    <p>
                      {totalScore >= 65 ? (
                        lang === 'da' 
                          ? 'Din samlede score er 65 point eller derover. Det indikerer, at der forekommer træk, som kan være forenelige med autismespektrumforstyrrelse. Resultatet er ikke i sig selv en diagnose og bør vurderes sammen med din udviklingshistorie, et klinisk interview og øvrige kliniske fund.'
                          : 'Your total score is 65 points or above. This indicates traits consistent with an autism spectrum condition. The result is not in itself a diagnosis and should always be evaluated alongside your developmental history, a clinical interview, and comprehensive clinical findings.'
                      ) : (
                        lang === 'da'
                          ? 'Din samlede score ligger under den oprindeligt validerede tærskelværdi på 65 point. Resultatet kan ikke alene udelukke autisme, og det skal altid vurderes i sammenhæng med din udviklingshistorie og en klinisk vurdering.'
                          : 'Your total score is below the validated threshold of 65 points. The result alone cannot rule out autism, and must always be considered in conjunction with your developmental history and a professional clinical assessment.'
                      )}
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-sand-medium/40">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-sand-medium/80 bg-sand-light text-petrol-dark font-medium text-xs sm:text-sm hover:bg-sand-medium/30 transition-colors cursor-pointer"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span>{lang === 'da' ? 'Start testen forfra' : 'Retake test'}</span>
                  </button>
                </div>
              </div>

              {/* HØJRE SIDEBAR: CTA & RAADS-R 4 OMRÅDER (lg:col-span-3) */}
              <div className="lg:col-span-3 space-y-4">
                
                {/* CTA til faglig vurdering */}
                <div className="bg-sand-medium/20 border border-sand-medium/60 rounded-2xl p-5 space-y-3">
                  <h4 className="font-display font-semibold text-base text-petrol-dark">
                    {lang === 'da' ? 'Ønsker du en faglig vurdering?' : 'Would you like a clinical assessment?'}
                  </h4>
                  <p className="text-xs text-text-muted leading-relaxed">
                    {lang === 'da' 
                      ? 'En selvscreening kan give et overblik, men kan ikke erstatte en autoriseret vurdering. Du er altid velkommen til at kontakte os.' 
                      : 'A self-screening provides an initial overview but cannot replace a formal clinical evaluation. You are always welcome to contact us.'}
                  </p>
                  <Link
                    to={localizePath('/kontakt')}
                    className="block text-center w-full px-4 py-2.5 bg-petrol-dark text-white font-semibold text-xs rounded-full hover:bg-petrol-medium transition-colors shadow-xs mt-2"
                  >
                    {lang === 'da' ? 'Kontakt os om booking' : 'Contact us for booking'}
                  </Link>
                </div>

                {/* RAADS-R undersøger fire områder */}
                <div className="bg-white border border-sand-medium/60 rounded-2xl p-5 space-y-3">
                  <h4 className="font-display font-semibold text-sm text-petrol-dark">
                    {lang === 'da' ? 'RAADS-R undersøger fire områder' : 'RAADS-R examines four key areas'}
                  </h4>
                  <ul className="space-y-2.5 text-xs text-text-muted">
                    <li className="flex items-center gap-2.5">
                      <Users className="w-4 h-4 text-petrol-medium shrink-0" />
                      <span>{lang === 'da' ? 'Social forståelse og relationer' : 'Social understanding & relationships'}</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <MessagesSquare className="w-4 h-4 text-petrol-medium shrink-0" />
                      <span>{lang === 'da' ? 'Sprog og kommunikation' : 'Language & communication'}</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <Activity className="w-4 h-4 text-petrol-medium shrink-0" />
                      <span>{lang === 'da' ? 'Sensorik og motorik' : 'Sensory & motor processing'}</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <Target className="w-4 h-4 text-petrol-medium shrink-0" />
                      <span>{lang === 'da' ? 'Afgrænsede interesser' : 'Circumscribed interests'}</span>
                    </li>
                  </ul>
                </div>

              </div>

            </div>

            {/* 7. AFSLUTTENDE "VIGTIGT AT VIDE" (OBLIGATORISK ANSVARSFRASKRIVELSE) */}
            <div className="bg-sand-medium/20 border border-sand-medium/60 rounded-2xl p-6 flex flex-col sm:flex-row items-start gap-4">
              <ShieldCheck className="w-6 h-6 text-petrol-medium shrink-0 mt-0.5" />
              <div className="space-y-1">
                <h4 className="font-display font-semibold text-base text-petrol-dark">
                  {lang === 'da' ? 'Vigtigt at vide' : 'Important to know'}
                </h4>
                <p className="text-xs sm:text-sm text-text-muted leading-relaxed">
                  {lang === 'da' 
                    ? 'RAADS-R er et screeningsværktøj og kan ikke i sig selv bruges til at stille en diagnose. Resultatet skal altid vurderes sammen med din udviklingshistorie, et klinisk interview og øvrige relevante kliniske fund.' 
                    : 'RAADS-R is a screening instrument and cannot be used alone to establish a formal diagnosis. Results must always be interpreted in the context of developmental history, clinical interviews, and other relevant diagnostic findings.'}
                </p>
              </div>
            </div>

          </div>
        )}

        {/* LÆS MERE OM AUTISME FOR STANDALONE SCREENING PAGE */}
        {!isEmbedded && (
          <div className="mt-16 sm:mt-20 border-t border-sand-medium/40 pt-8 sm:pt-12">
            <ReadMoreAutismSection className="bg-transparent border-t-0 py-0 px-0" />
          </div>
        )}

      </div>
    </div>
  );
}
